import type {
  TenantProgram,
  TenantProgramRegistrationCategoryOption,
  TenantProgramRegistrationSessionOption,
  TenantProgramUpsertPayload,
} from '@tark/shared/types';
import type {
  ProgramCatalogCategoryDraft,
  ProgramCatalogFormState,
  ProgramCatalogSessionDraft,
  ProgramCatalogSubmitIntent,
} from './programCatalogTypes';

export const sportOptions = [
  { value: 'TENNIS', label: 'Tennis' },
  { value: 'PICKLEBALL', label: 'Pickleball' },
  { value: 'BOTH', label: 'Both' },
];

export const programTypeOptions = [
  { value: 'PRIVATE_LESSON', label: 'Private lesson' },
  { value: 'GROUP_CLINIC', label: 'Group clinic' },
  { value: 'TEAM_TRAINING', label: 'Team training' },
  { value: 'CAMP', label: 'Camp' },
  { value: 'OPEN_PLAY', label: 'Open play' },
];

export const ageBandOptions = [
  { value: '', label: 'Any age' },
  { value: 'KIDS', label: 'Kids' },
  { value: 'JUNIORS', label: 'Juniors' },
  { value: 'ADULTS', label: 'Adults' },
  { value: 'SENIORS', label: 'Seniors' },
];

export function createDraftId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function createSessionDraft(index: number = 0): ProgramCatalogSessionDraft {
  return {
    id: createDraftId('session'),
    label: index === 0 ? 'Week 1' : `Week ${index + 1}`,
    dateLabel: '',
    startsOn: '',
    endsOn: '',
    scheduleLabel: '',
    capacity: '',
    status: 'OPEN',
  };
}

export function createCategoryDraft(index: number = 0): ProgramCatalogCategoryDraft {
  return {
    id: createDraftId('class'),
    label: index === 0 ? 'Junior class' : `Class option ${index + 1}`,
    ageLabel: '',
    levelLabel: '',
    priceAmount: '',
    priceLabel: '',
    coachUserId: '',
    coachLabel: '',
    scheduleLabel: '',
    daysRule: '',
    capacity: '',
    status: 'OPEN',
  };
}

export function defaultProgramCatalogForm(program?: TenantProgram | null): ProgramCatalogFormState {
  const policy = program?.registrationPolicy;
  const sessions = (policy?.sessionOptions ?? []).map(toSessionDraft);
  const categories = (policy?.categoryOptions ?? []).map(toCategoryDraft);

  return {
    name: program?.name ?? '',
    publicTitle: policy?.publicTitle ?? program?.name ?? '',
    seasonLabel: policy?.seasonLabel ?? '',
    startsOn: policy?.startsOn ?? '',
    endsOn: policy?.endsOn ?? '',
    locationLabel: policy?.locationLabel ?? '',
    audienceLabel: policy?.audienceLabel ?? '',
    publicNote: policy?.registrationNote ?? program?.description ?? '',
    sportCode: program?.sportCode ?? 'TENNIS',
    programType: program?.programType ?? 'CAMP',
    ageBandCode: program?.ageBandCode ?? '',
    defaultCapacity: program?.defaultCapacity != null ? String(program.defaultCapacity) : '',
    priceLabel: policy?.priceLabel ?? '',
    requireDaysPreference: Boolean(policy?.requireDaysPreference),
    notifyFamilies: policy?.notifyExistingFamiliesOnPublish !== false,
    sessions: sessions.length ? sessions : [createSessionDraft()],
    categories: categories.length ? categories : [createCategoryDraft()],
  };
}

export function buildProgramCatalogPayload(
  form: ProgramCatalogFormState,
  intent: ProgramCatalogSubmitIntent
): TenantProgramUpsertPayload {
  return {
    name: form.name.trim(),
    description: trimOrNull(form.publicNote),
    sportCode: form.sportCode,
    programType: form.programType,
    ageBandCode: form.ageBandCode || null,
    capacityMode: 'FIXED',
    defaultCapacity: toPositiveNumber(form.defaultCapacity),
    registrationPolicy: {
      enabled: intent === 'publish',
      publicTitle: form.publicTitle.trim() || form.name.trim(),
      seasonLabel: trimOrNull(form.seasonLabel),
      startsOn: trimOrNull(form.startsOn),
      endsOn: trimOrNull(form.endsOn),
      locationLabel: trimOrNull(form.locationLabel),
      audienceLabel: trimOrNull(form.audienceLabel),
      priceLabel: trimOrNull(form.priceLabel),
      registrationNote: trimOrNull(form.publicNote),
      currencyCode: 'USD',
      requireStudentName: true,
      requireDaysPreference: form.requireDaysPreference,
      notifyExistingFamiliesOnPublish: form.notifyFamilies,
      sessionOptions: form.sessions
        .filter((option) => option.label.trim())
        .map(toSessionOption),
      categoryOptions: form.categories
        .filter((option) => option.label.trim())
        .map(toCategoryOption),
    },
  };
}

export function validateProgramCatalogForm(
  form: ProgramCatalogFormState,
  intent: ProgramCatalogSubmitIntent
): string | null {
  if (form.name.trim().length < 2) {
    return 'Add a class or program name.';
  }

  const capacity = form.defaultCapacity ? Number(form.defaultCapacity) : null;
  if (capacity != null && (!Number.isFinite(capacity) || capacity < 1)) {
    return 'Max slots must be a positive number.';
  }

  if (form.startsOn && form.endsOn && form.startsOn > form.endsOn) {
    return 'Start date must be before end date.';
  }

  for (const session of form.sessions) {
    if (session.startsOn && session.endsOn && session.startsOn > session.endsOn) {
      return `${session.label || 'Session'} has an invalid date range.`;
    }
    if (session.capacity && Number(session.capacity) < 1) {
      return `${session.label || 'Session'} capacity must be positive.`;
    }
  }

  for (const category of form.categories) {
    if (category.capacity && Number(category.capacity) < 1) {
      return `${category.label || 'Class'} capacity must be positive.`;
    }
    if (category.priceAmount && Number(category.priceAmount) < 0) {
      return `${category.label || 'Class'} fee cannot be negative.`;
    }
  }

  if (intent === 'draft') {
    return null;
  }

  if (!form.startsOn || !form.endsOn) {
    return 'Add the season start and end dates before publishing.';
  }
  if (!form.defaultCapacity) {
    return 'Add max slots before publishing.';
  }
  if (!form.sessions.some((option) => option.label.trim())) {
    return 'Add at least one session or week option before publishing.';
  }
  if (!form.categories.some((option) => option.label.trim())) {
    return 'Add at least one age or class option before publishing.';
  }
  if (!form.priceLabel.trim() && !form.categories.some((option) => option.priceAmount || option.priceLabel.trim())) {
    return 'Add a fee before publishing.';
  }

  return null;
}

export function programIsPublished(program: TenantProgram): boolean {
  return Boolean(program.registrationPolicy?.enabled);
}

export function formatCatalogDateRange(program: TenantProgram): string {
  const policy = program.registrationPolicy;
  if (policy?.startsOn && policy?.endsOn) {
    return `${formatDate(policy.startsOn)} - ${formatDate(policy.endsOn)}`;
  }
  if (policy?.seasonLabel) {
    return policy.seasonLabel;
  }
  return 'Dates not set';
}

export function formatScheduleCodeLabel(value?: string | null): string {
  if (!value) {
    return '';
  }
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function summarizeProgramOptions(program: TenantProgram) {
  const policy = program.registrationPolicy;
  return {
    sessionCount: policy?.sessionOptions?.length ?? 0,
    categoryCount: policy?.categoryOptions?.length ?? 0,
    priceLabel: policy?.priceLabel ?? null,
    locationLabel: policy?.locationLabel ?? null,
  };
}

function toSessionDraft(option: TenantProgramRegistrationSessionOption): ProgramCatalogSessionDraft {
  return {
    id: createDraftId('session'),
    recordId: option.recordId,
    optionId: option.optionId,
    label: option.label ?? '',
    dateLabel: option.dateLabel ?? '',
    startsOn: option.startsOn ?? '',
    endsOn: option.endsOn ?? '',
    scheduleLabel: option.scheduleLabel ?? '',
    capacity: option.capacity != null ? String(option.capacity) : '',
    status: option.status ?? 'OPEN',
  };
}

function toCategoryDraft(option: TenantProgramRegistrationCategoryOption): ProgramCatalogCategoryDraft {
  return {
    id: createDraftId('class'),
    recordId: option.recordId,
    optionId: option.optionId,
    label: option.label ?? '',
    ageLabel: option.ageLabel ?? '',
    levelLabel: option.levelLabel ?? '',
    priceAmount: option.priceAmount != null ? String(option.priceAmount) : '',
    priceLabel: option.priceLabel ?? '',
    coachUserId: option.coachUserId != null ? String(option.coachUserId) : '',
    coachLabel: option.coachLabel ?? '',
    scheduleLabel: option.scheduleLabel ?? '',
    daysRule: option.daysRule ?? '',
    capacity: option.capacity != null ? String(option.capacity) : '',
    status: option.status ?? 'OPEN',
  };
}

function toSessionOption(option: ProgramCatalogSessionDraft, index: number): TenantProgramRegistrationSessionOption {
  return {
    recordId: option.recordId,
    optionId: option.optionId || `session-${index + 1}`,
    label: option.label.trim(),
    dateLabel: trimOrNull(option.dateLabel),
    startsOn: trimOrNull(option.startsOn),
    endsOn: trimOrNull(option.endsOn),
    scheduleLabel: trimOrNull(option.scheduleLabel),
    capacity: toPositiveNumber(option.capacity),
    status: option.status || 'OPEN',
  };
}

function toCategoryOption(option: ProgramCatalogCategoryDraft, index: number): TenantProgramRegistrationCategoryOption {
  return {
    recordId: option.recordId,
    optionId: option.optionId || `category-${index + 1}`,
    label: option.label.trim(),
    ageLabel: trimOrNull(option.ageLabel),
    levelLabel: trimOrNull(option.levelLabel),
    priceAmount: toPositiveNumber(option.priceAmount),
    priceLabel: trimOrNull(option.priceLabel),
    coachUserId: toPositiveNumber(option.coachUserId),
    coachLabel: trimOrNull(option.coachLabel),
    scheduleLabel: trimOrNull(option.scheduleLabel),
    daysRule: trimOrNull(option.daysRule),
    capacity: toPositiveNumber(option.capacity),
    status: option.status || 'OPEN',
  };
}

function toPositiveNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function trimOrNull(value?: string | null): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed : null;
}

function formatDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return value;
  }
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
